// The Tasks panel (tasks.md section 13): each open tree Quanty One works through, its tasks and progress,
// Stop and Resume, and the Work page in AlphaLab. Read when opened and every 10 seconds.
import { X } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { get, post } from "../api.js";
import { progressLine, stopLine, taskLabel, type TasksView } from "../tasks.js";
import { Button } from "./ui/button.js";

export interface TasksPanelProps {
  alphalabUrl?: string;
  onSignedOut(): void;
  onClose(): void;
  onOpenAlphaLab(url: string): void;
}

export function TasksPanel({ alphalabUrl, onSignedOut, onClose, onOpenAlphaLab }: TasksPanelProps) {
  const [view, setView] = useState<TasksView>();
  const [notice, setNotice] = useState<string>();

  const load = useCallback(async () => {
    try {
      const res = await get<TasksView>("/api/tasks");
      if (res.status === 401) return onSignedOut();
      if (res.status === 200) return setView(res.data);
    } catch {
      // Fall through.
    }
    setNotice("Your tasks could not be loaded.");
  }, [onSignedOut]);

  useEffect(() => {
    void load();
    const timer = setInterval(() => void load(), 10_000);
    return () => clearInterval(timer);
  }, [load]);

  const act = async (path: string, body?: unknown): Promise<void> => {
    setNotice(undefined);
    try {
      const res = await post(path, body);
      if (res.status === 401) return onSignedOut();
      if (res.status !== 200) setNotice("That did not go through. Try again.");
    } catch {
      setNotice("That did not go through. Try again.");
    }
    await load();
  };

  const trees = (view?.trees ?? []).filter((t) => t.state !== "finished");
  const paused = view?.driver.state === "paused";
  const work = alphalabUrl ? `${alphalabUrl.replace(/\/$/, "")}/work` : undefined;

  return (
    <aside className="flex h-full w-full max-w-sm shrink-0 flex-col border-l border-border bg-card" aria-label="Tasks">
      <div className="flex h-10 shrink-0 items-center gap-2 border-b border-border px-3">
        <span className="flex-1 text-sm font-semibold">Tasks</span>
        {paused && (
          <Button variant="outline" size="sm" onClick={() => void act("/api/tasks/resume")}>
            Resume
          </Button>
        )}
        {view?.driver.state === "active" && trees.length > 0 && (
          <Button variant="outline" size="sm" onClick={() => void act("/api/tasks/pause")}>
            Stop
          </Button>
        )}
        <Button variant="ghost" size="icon-sm" aria-label="Close Tasks" onClick={onClose}>
          <X />
        </Button>
      </div>
      <div className="flex-1 space-y-2 overflow-y-auto p-3">
        {notice && <p className="rounded-md bg-error-bg px-3 py-2 text-sm text-error">{notice}</p>}
        {!view && !notice && <p className="text-sm text-muted-foreground">Loading…</p>}
        {view && !view.live && view.snapshot && <pre className="whitespace-pre-wrap text-xs text-muted-foreground">{view.snapshot}</pre>}
        {view?.live && trees.length === 0 && (
          <p className="text-sm text-muted-foreground">No open tasks. Ask Quanty One for work with several steps and it tracks it here.</p>
        )}
        {paused && <p className="text-xs text-muted-foreground">Paused. Quanty One starts no task turn until you resume.</p>}
        {trees.map((tree) => {
          const counters = view?.driver.trees[tree.rootTaskId];
          const stopped = stopLine(counters);
          return (
            <div key={tree.rootTaskId} className="rounded-lg border border-border bg-background p-3 text-sm">
              <div className="font-medium">{tree.request}</div>
              <div className="mt-1 text-xs text-muted-foreground">{progressLine(tree, Date.now())}</div>
              {stopped && <div className="mt-1 text-xs text-warning">{stopped}</div>}
              <ul className="mt-2 space-y-1">
                {tree.tasks.map((task) => (
                  <li key={task.taskId} className="text-xs">
                    <span className="font-medium">{taskLabel(task.label)}</span> · {task.description}
                    {task.label === "waiting" && task.waitingOn.length > 0 && (
                      <span className="text-muted-foreground"> · {task.waitingOn.map((w) => `${w.handle} (${w.status})`).join(", ")}</span>
                    )}
                    {task.question && <div className="text-muted-foreground">{task.question}</div>}
                    {task.result && <div className="text-muted-foreground">{task.result}</div>}
                  </li>
                ))}
              </ul>
              <div className="mt-2 flex flex-wrap gap-2">
                {counters?.stoppedBy === "stuck" && !paused && (
                  <Button variant="outline" size="sm" onClick={() => void act("/api/tasks/resume")}>
                    Resume
                  </Button>
                )}
                {work && (
                  <Button variant="ghost" size="sm" onClick={() => onOpenAlphaLab(work)}>
                    Open in AlphaLab
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </aside>
  );
}
